import type { ModelData } from '@/types/models';
import { formatCurrency } from '@/utils/formatters';

interface CashFlowTabProps {
  data: ModelData;
}

export function CashFlowTab({ data }: CashFlowTabProps) {
  const years = data?.years || [];
  const cashFlow = data?.cashFlow || [];
  const detailedCashFlow = data?.detailedCashFlow || [];

  // Handle case where data is not yet loaded
  if (!years.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-4xl mb-3">💰</div>
          <p className="text-slate-500">No cash flow data available</p>
        </div>
      </div>
    );
  }

  // If we have detailed cash flow data, use that
  if (detailedCashFlow.length > 0) {
    const rows = [
      { key: 'netIncome', label: 'Net Income', bold: false, section: 'operating' },
      { key: 'depreciation', label: 'Depreciation', bold: false, section: 'operating' },
      { key: 'amortization', label: 'Amortization', bold: false, section: 'operating' },
      { key: 'workingCapitalChange', label: 'Working Capital Changes', bold: false, section: 'operating' },
      { key: 'operatingCashFlow', label: 'Operating Cash Flow', bold: true, section: 'operating', highlight: true },
      { key: 'totalCapex', label: 'Capital Expenditures', bold: false, section: 'investing' },
      { key: 'investingCashFlow', label: 'Investing Cash Flow', bold: true, section: 'investing', highlight: true },
      { key: 'capitalRaised', label: 'Capital Raised', bold: false, section: 'financing' },
      { key: 'annualDebtPayment', label: 'Debt Payments', bold: false, section: 'financing' },
      { key: 'financingCashFlow', label: 'Financing Cash Flow', bold: true, section: 'financing', highlight: true },
      { key: 'netCashChange', label: 'Net Change in Cash', bold: true, section: 'net', primary: true },
    ];

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-3 px-4 font-semibold text-slate-700 bg-slate-50">
                Cash Flow Item
              </th>
              {years.map((year) => (
                <th
                  key={year}
                  className="text-right py-3 px-4 font-semibold text-slate-700 bg-slate-50"
                >
                  Year {year}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr
                key={row.key}
                className={`hover:bg-slate-50 ${
                  row.primary
                    ? 'bg-primary-50 border-t-2 border-slate-300'
                    : row.highlight
                    ? 'bg-slate-50'
                    : ''
                }`}
              >
                <td
                  className={`py-3 px-4 ${row.bold ? 'font-semibold' : ''} ${
                    row.primary ? 'text-primary-900' : 'text-slate-900'
                  }`}
                >
                  {row.label}
                </td>
                {detailedCashFlow.map((yearData, i) => {
                  const value = (yearData?.[row.key as keyof typeof yearData] as number) || 0;
                  const isNegative = value < 0;
                  return (
                    <td
                      key={i}
                      className={`py-3 px-4 text-right ${
                        row.bold ? 'font-semibold' : ''
                      } ${
                        row.primary
                          ? 'text-primary-900'
                          : isNegative
                          ? 'text-red-600'
                          : 'text-slate-700'
                      }`}
                    >
                      {formatCurrency(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Simple view with just net cash flow per year
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-3 px-4 font-semibold text-slate-700 bg-slate-50">
              Cash Flow
            </th>
            {years.map((year) => (
              <th
                key={year}
                className="text-right py-3 px-4 font-semibold text-slate-700 bg-slate-50"
              >
                Year {year}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="bg-primary-50">
            <td className="py-3 px-4 font-semibold text-primary-900">
              Net Cash Flow
            </td>
            {cashFlow.map((value: number, i: number) => {
              const isNegative = value < 0;
              return (
                <td
                  key={i}
                  className={`py-3 px-4 text-right font-semibold ${
                    isNegative ? 'text-red-600' : 'text-green-600'
                  }`}
                >
                  {formatCurrency(value)}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
